import Image from "next/image";

import type { SanityImageSource } from "@sanity/image-url";
import cn from "classnames";

import { Text } from "@/components/text/text.component";

import { urlFor } from "@/sanity/utils";

import type { ComparisonColumn } from "./appRatingsComparisonTable.types";

/** Shared typography for brand / column title cells (subtitle1 sizing, responsive). */
const COLUMN_HEADER_SUBTITLE_METRICS =
  "block w-full leading-[1.15] max-lg:!text-[14px] max-lg:pb-0 lg:!text-[20px] lg:pb-2";

function headerImageSrc(column: ComparisonColumn): string | null {
  const url = column.headerImage?.url;
  if (url) return url;
  if (column.headerImage?.asset) {
    return urlFor(column.headerImage as SanityImageSource)
      .width(96)
      .height(96)
      .url();
  }
  return null;
}

export function ColumnHeader({
  column,
  headerAlign = "center",
}: ColumnHeaderProps) {
  const src = headerImageSrc(column);
  const text = column.headerText?.trim();
  const alt = column.headerImage?.alt?.trim() || text || "Brand";
  const isStart = headerAlign === "start";

  if (src) {
    return (
      <div
        role="cell"
        className={cn(
          "relative aspect-square h-12 w-12 shrink-0",
          isStart ? "mr-auto" : "mx-auto",
        )}>
        <span className="sr-only">{alt}</span>
        <Image
          src={src}
          alt={alt}
          fill
          className="object-contain object-center"
          sizes="48px"
        />
      </div>
    );
  }

  if (text) {
    return (
      <Text
        variant="subtitle1"
        component="span"
        className={cn(
          "text-greyscale-headline whitespace-normal",
          COLUMN_HEADER_SUBTITLE_METRICS,
          isStart
            ? "max-w-none text-left"
            : "mx-auto max-w-[7rem] self-stretch text-center sm:max-w-[10rem]",
        )}>
        {text}
      </Text>
    );
  }

  return (
    <Text
      variant="subtitle1"
      component="span"
      className={cn(
        "text-greyscale-strength",
        COLUMN_HEADER_SUBTITLE_METRICS,
        isStart ? "text-left" : "self-stretch text-center",
      )}>
      —
    </Text>
  );
}

type ColumnHeaderProps = {
  column: ComparisonColumn;
  headerAlign?: "center" | "start";
};
